// Smazání testovacího kontaktu i se vším, co z něj vzniklo (hlášení #190, 1. 11. 2026).
// Jen owner. Server: /api/lead-delete { id, testovaci: true, nahled | potvrzeni }.
//   LeadNejdeSmazatDialog: běžné smazání vrátilo 409, nabídne Archivovat nebo Smazat testovací.
//   SmazatTestovaciDialog: náhled toho, co se smaže (nebo proč to nejde), a červené potvrzení.
import React, { useEffect, useState } from "react";
import { Trash2, Archive, CalendarDays, Receipt, UserRound, MessageSquare, History, ClipboardList, Handshake } from "lucide-react";
import { Modal, HlavickaOkna, Field } from "../ui.jsx";
import { apiFetch } from "../../lib/api.js";
import { Oznameni, Pil } from "./salesUi.jsx";
import { denKratce, casBezNuly } from "./salesApi.js";

// Tři tlačítka se v patce okna neroztahují samy (patka-okna), na telefonu jsou proto
// každé přes celou šířku pod sebou, Zpět dole.
const naTelefonuCele = "w-full sm:w-auto";

export function LeadNejdeSmazatDialog({ open, jmeno, duvod, onArchivovat, onTestovaci, onClose }) {
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={Trash2} nazev="Lead nejde smazat" popis={jmeno} onClose={onClose} />}
      footer={<>
        <button type="button" className={`btn-secondary order-last sm:order-none sm:mr-auto ${naTelefonuCele}`} onClick={onClose}>Zpět</button>
        <button type="button" className={`btn border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 ${naTelefonuCele}`} onClick={onTestovaci}><Trash2 className="h-4 w-4" />Smazat testovací kontakt</button>
        <button type="button" className={`btn-primary order-first sm:order-none ${naTelefonuCele}`} onClick={onArchivovat}><Archive className="h-4 w-4" />Archivovat</button>
      </>}>
      <div className="space-y-3">
        <Oznameni ton="pozor">{duvod}</Oznameni>
        <p className="text-[15px] leading-snug text-slate-700"><b>Archivovat:</b> zmizí z plánu volání i z přehledů leadů, historie zůstane. Vrátit ho jde tlačítkem Vrátit z archivu na kartě kontaktu.</p>
        <p className="text-[15px] leading-snug text-slate-700"><b>Smazat testovací kontakt:</b> jen pro testovací data. Smaže kontakt i jeho zkušební, rezervace, odměnu a klientku, která z něj vznikla. Nejdřív uvidíš, co přesně se smaže.</p>
      </div>
    </Modal>
  );
}

function Radek({ ikona: Ikona, nazev, children, zustane = false }) {
  return (
    <div className="flex items-start gap-3 border-t border-slate-100 py-2.5 first:border-t-0">
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${zustane ? "bg-slate-100 text-slate-600" : "bg-rose-50 text-rose-700"}`}><Ikona className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1 space-y-1.5 pt-0.5">
        <div className="text-[15px] font-semibold text-slate-900">{nazev}</div>
        <div className="flex flex-wrap gap-1.5">{children}</div>
      </div>
    </div>
  );
}

// Stejné pravidlo jako server (potvrzeniSedi v _lib/smazaniTestovacihoLeadu.cjs):
// trim a bez ohledu na velikost písmen. Lead bez jména se potvrzuje napsáním id.
const potvrzovaci = (n) => String(n?.potvrzeni || n?.lead?.jmeno || n?.lead?.id || "").trim();
const sedi = (napsano, ocekavano) => {
  const a = String(napsano || "").trim().toLocaleLowerCase("cs");
  return !!a && a === String(ocekavano || "").toLocaleLowerCase("cs");
};

const STAV_ZK = { aktivni: "aktivní", zruseno: "zrušený", probehl: "proběhl", nedorazila: "nedorazila", vyprselo: "vypršelo", ceka_platbu: "čeká na platbu" };

export function SmazatTestovaciDialog({ leadId, open, onClose, onSmazano }) {
  const [n, setN] = useState(null);
  const [chyba, setChyba] = useState("");
  const [busy, setBusy] = useState(false);
  const [napsano, setNapsano] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let zivy = true;
    setN(null); setChyba(""); setBusy(false); setNapsano("");
    (async () => {
      try {
        const r = await apiFetch("/api/lead-delete", { method: "POST", body: JSON.stringify({ id: leadId, testovaci: true, nahled: true }) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `Chyba ${r.status}`);
        if (zivy) setN(j);
      } catch (e) { if (zivy) setChyba(e.message); }
    })();
    return () => { zivy = false; };
  }, [open, leadId]);

  const ocekavano = potvrzovaci(n);
  const bezJmena = !!n && !String(n.lead.jmeno || "").trim();
  const potvrzeno = !!n && sedi(napsano, ocekavano);
  const smazat = async () => {
    if (!n || n.blokovano || !potvrzeno) return;
    setBusy(true); setChyba("");
    try {
      const r = await apiFetch("/api/lead-delete", { method: "POST", body: JSON.stringify({ id: leadId, testovaci: true, potvrzeni: napsano.trim() }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Chyba ${r.status}`);
      onSmazano?.(j);
    } catch (e) { setChyba(e.message); setBusy(false); }
  };

  const rezKlientky = (n && n.rezervaceKlientky) || [];
  const nic = n && !n.zkusebni.length && !n.rezervace.length && !rezKlientky.length && !n.odmeny.length && !n.klientky.length && !n.deal;
  return (
    <Modal open={open} onClose={busy ? undefined : onClose} size="sm" plnaHlavicka
      header={<HlavickaOkna ikona={Trash2} nazev="Smazat testovací kontakt" popis={n ? n.lead.jmeno : "Načítám, co se smaže"} onClose={busy ? () => {} : onClose} />}
      footer={<>
        <button type="button" className="btn-secondary sm:mr-auto" onClick={onClose} disabled={busy}>Zpět</button>
        {n && !n.blokovano && (
          <button type="button" className="btn-danger" onClick={smazat} disabled={busy || !potvrzeno} aria-busy={busy}><Trash2 className="h-4 w-4" />{busy ? "Mažu" : "Smazat natrvalo"}</button>
        )}
      </>}>
      <div className="space-y-3">
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        {!n && !chyba && [0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-brand-light" />)}
        {n && n.blokovano && (
          <Oznameni ton="problem">
            <b>Tenhle kontakt zatím smazat nejde.</b>
            {/* 3. 11. 2026: každá blokace na vlastním řádku, říká přesně, co udělat. */}
            <ul className="mt-1.5 list-disc space-y-1 pl-4">{n.blokace.map((b, i) => <li key={i}>{b}</li>)}</ul>
            <div className="mt-1.5">Faktury ve FAPI se nikdy nemažou. Když to udělat nechceš, kontakt místo smazání archivuj.</div>
          </Oznameni>
        )}
        {n && (n.storna || []).length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white px-3">
            <Radek ikona={Receipt} zustane nazev={n.storna.length === 1 ? "Stornovaná faktura, nesmaže se" : `Stornované faktury, nesmažou se (${n.storna.length})`}>
              {n.storna.map((f, i) => <Pil key={`${f.cislo}-${i}`} ton="neutralni" casti={[f.cislo, "storno, zůstane ve FAPI"]} />)}
            </Radek>
          </div>
        )}
        {n && !n.blokovano && (
          <>
            <Oznameni ton="problem"><b>Smaže se natrvalo, vrátit to nejde.</b> Používej jen u testovacích kontaktů.</Oznameni>
            <div className="rounded-2xl border border-slate-200 bg-white px-3">
              <Radek ikona={UserRound} nazev="Kontakt">
                <Pil ton="problem" casti={[n.lead.jmeno, `id ${n.lead.id}`]} />
                {n.lead.stage && <Pil ton="neutralni" casti={n.lead.stage} />}
              </Radek>
              <Radek ikona={MessageSquare} nazev="Historie a komentáře">
                <Pil ton="neutralni" casti={["Historie", String(n.udalosti)]} />
                <Pil ton="neutralni" casti={["Komentáře", String(n.komentare)]} />
              </Radek>
              {n.zkusebni.length > 0 && (
                <Radek ikona={History} nazev={n.zkusebni.length === 1 ? "Zkušební trénink" : `Zkušební tréninky (${n.zkusebni.length})`}>
                  {n.zkusebni.map((z) => <Pil key={z.id} ton="problem" casti={[z.datum ? `${denKratce(z.datum)} ${casBezNuly(z.cas)}`.trim() : "bez termínu", z.trener, STAV_ZK[z.stav] || z.stav]} />)}
                </Radek>
              )}
              {n.rezervace.length > 0 && (
                <Radek ikona={CalendarDays} nazev={n.rezervace.length === 1 ? "Rezervace v kalendáři" : `Rezervace v kalendáři (${n.rezervace.length})`}>
                  {n.rezervace.map((r) => <Pil key={r.booking_id} ton="problem" casti={[`${denKratce(r.datum)} ${casBezNuly(r.cas)}`.trim(), r.trener]} />)}
                </Radek>
              )}
              {rezKlientky.length > 0 && (
                <Radek ikona={CalendarDays} nazev={rezKlientky.length === 1 ? "Rezervace klientky" : `Rezervace klientky (${rezKlientky.length})`}>
                  {rezKlientky.map((r) => <Pil key={r.booking_id} ton="problem" casti={[r.datum ? `${denKratce(r.datum)} ${casBezNuly(r.cas)}`.trim() : "bez termínu", r.trener]} />)}
                  {n.serie > 0 && <Pil ton="problem" casti={["Opakovaná série se ukončí", String(n.serie)]} />}
                </Radek>
              )}
              {!rezKlientky.length && n.serie > 0 && (
                <Radek ikona={CalendarDays} nazev="Opakované rezervace klientky">
                  <Pil ton="problem" casti={["Série se ukončí", String(n.serie)]} />
                </Radek>
              )}
              {n.deal > 0 && (
                <Radek ikona={Handshake} nazev="Deal u trenéra">
                  <Pil ton="problem" casti={["Zápis výsledku", String(n.deal)]} />
                  {n.ukoly > 0 && <Pil ton="problem" casti={["Úkol trenérovi", String(n.ukoly)]} />}
                </Radek>
              )}
              {n.odmeny.length > 0 && (
                <Radek ikona={Receipt} nazev={n.odmeny.length === 1 ? "Odměna" : `Odměny (${n.odmeny.length})`}>
                  {n.odmeny.map((o) => <Pil key={o.id} ton="problem" casti={[o.popis.split(":")[0], `${o.castka.toLocaleString("cs-CZ")} Kč`]} />)}
                </Radek>
              )}
              {n.klientky.length > 0 && (
                <Radek ikona={ClipboardList} nazev="Klientka vzniklá z kontaktu, smaže se celá">
                  {n.klientky.map((k) => <Pil key={k.id} ton="problem" casti={k.jmeno} />)}
                </Radek>
              )}
            </div>
            {nic && <Oznameni ton="info">Kromě kontaktu, historie a komentářů na něm nic dalšího není.</Oznameni>}
          </>
        )}
        {n && n.zustanou.length > 0 && (
          <Oznameni ton="info">
            <b>Zůstane beze změny:</b> {n.zustanou.map((k) => `${k.jmeno} (${k.duvod})`).join(", ")}.
          </Oznameni>
        )}
        {n && !n.blokovano && n.upozorneni.map((u, i) => <Oznameni key={i} ton="pozor">{u}</Oznameni>)}
        {n && !n.blokovano && (
          <Field label={bezJmena ? "Pro potvrzení napiš id kontaktu" : "Pro potvrzení napiš jméno kontaktu"}
            hint={<>Napiš <b className="text-slate-700">{ocekavano}</b>, teprve pak půjde smazat.</>}>
            <input className="field" value={napsano} onChange={(e) => setNapsano(e.target.value)} disabled={busy}
              autoComplete="off" autoCorrect="off" spellCheck={false} inputMode={bezJmena ? "numeric" : undefined}
              aria-invalid={napsano.trim() !== "" && !potvrzeno}
              onKeyDown={(e) => { if (e.key === "Enter" && potvrzeno && !busy) smazat(); }} />
          </Field>
        )}
      </div>
    </Modal>
  );
}
